import type { DeviceInfo } from '../../services/admin';
import { Field } from './DeviceDetailsParts';
import WifiControls from './WifiControls';
import WifiSavedNetworks from './WifiSavedNetworks';
import { ageText, cellularSignalText, isStale, wifiBand, wifiStateText } from './wifiNetworkText';

// Device Details "Wifi Network" facts and the "Mobile Network" signal field (documentation/
// protocol.md "Wi-Fi remote control", "Cellular signal"). Only values the radio measured; anything
// it did not report is "Not available", and an old reading is marked stale rather than shown as now.

function Freshness({ label, at, now }: { label: string; at: string | null | undefined; now: number }) {
  const stale = isStale(at, now);
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase font-semibold">{label}</p>
      <p className="text-white">
        {at ? `${new Date(at).toLocaleString()} (${ageText(at, now)})` : 'Not available'}
        {at && stale && <span className="ml-2 text-xs text-amber-400 border border-amber-600 rounded px-1">stale</span>}
      </p>
    </div>
  );
}

export function WifiNetworkFacts({ info, now }: { info: DeviceInfo; now: number }) {
  const associated = info.wifi_enabled === true && !!info.wifi_ssid;
  const addresses = associated ? info.wifi_ip_addresses ?? [] : [];
  return (
    <>
      <Field label="Wi-Fi" value={wifiStateText(info)} />
      <Field label="Current SSID" value={associated ? info.wifi_ssid : null} />
      <Field label="Signal strength (Wi-Fi RSSI)" value={associated && info.wifi_signal_dbm != null ? `${info.wifi_signal_dbm} dBm` : null} />
      <Field label="Link speed" value={associated && info.wifi_link_speed_mbps != null ? `${info.wifi_link_speed_mbps} Mbps` : null} />
      <Field label="Band / frequency" value={associated ? wifiBand(info.wifi_frequency_mhz) : null} />
      <Field label="BSSID" value={associated ? info.wifi_bssid : null} />
      <Field label="Security" value={associated ? info.wifi_security : null} />
      <Freshness label="Last Wi-Fi report" at={info.wifi_telemetry_updated_at} now={now} />
      <Field
        label="Last Wi-Fi operation"
        value={info.wifi_operation_state
          ? `${info.wifi_operation_state}${info.wifi_operation_message ? `: ${info.wifi_operation_message}` : ''}${info.wifi_operation_updated_at ? ` (${ageText(info.wifi_operation_updated_at, now)})` : ''}`
          : null}
      />
      <div className="col-span-2 md:col-span-3">
        <p className="text-xs text-gray-400 uppercase font-semibold">Wi-Fi addresses</p>
        {addresses.length > 0 ? (
          <ul className="text-white list-disc list-inside">
            {addresses.map((ip) => <li key={ip} className="font-mono text-sm">{ip}</li>)}
          </ul>
        ) : (
          <p className="text-white">Not available</p>
        )}
      </div>
    </>
  );
}

export function CellularSignalField({ info, now }: { info: DeviceInfo; now: number }) {
  const at = info.cellular_signal_updated_at;
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase font-semibold">Signal strength</p>
      <p className="text-white">
        {cellularSignalText(info)}
        {at && <span className="text-gray-400 text-xs"> · {ageText(at, now)}</span>}
        {at && isStale(at, now) && <span className="ml-2 text-xs text-amber-400 border border-amber-600 rounded px-1">stale</span>}
      </p>
    </div>
  );
}

// The Device Details "Wifi Network" section: facts, the shared Wi-Fi controls (scan / on / off /
// connect for this radio) and the radio-reported saved networks.
export function WifiNetworkSection({ info, now, onInfo }: { info: DeviceInfo; now: number; onInfo: (info: DeviceInfo) => void }) {
  // The server is the authority (409 when the radio is not connected); low_battery etc. are online.
  const online = info.status !== 'offline';
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 space-y-3">
      <h3 className="text-lg font-bold">Wifi Network</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <WifiNetworkFacts info={info} now={now} />
      </div>
      <div className="border-t border-gray-700 pt-3">
        <WifiControls radioId={info.id} online={online} info={info} onInfo={onInfo} />
      </div>
      <div className="border-t border-gray-700 pt-3 space-y-2">
        <p className="text-xs text-gray-400 uppercase font-semibold">Saved networks (reported by the radio)</p>
        <WifiSavedNetworks radioId={info.id} info={info} online={online} onInfo={onInfo} />
      </div>
    </div>
  );
}
